import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { HelpPanel } from "@/components/HelpPanel";
import { FloatingHelpButton } from "@/components/FloatingHelpButton";
import { ProfilePhotoButton } from "@/components/ProfilePhotoButton";
import { MyStatesButton } from "@/components/MyStatesButton";

import { useAuth } from "@/context/AuthContext";
import api, { LOGOS } from "@/lib/api";
import { APP_VERSION } from "@/lib/version";
import { hasUnseenChangelog, markChangelogSeen } from "@/lib/changelog";
import {
  LayoutDashboard, GraduationCap, Building2, Users, FileCheck2, Video,
  CalendarDays, BarChart3, ScrollText, Bell, LogOut, Briefcase, School,
  Settings, FileText, Stethoscope, Menu, X, HelpCircle, MessageSquare, Coffee, Megaphone, Eye, HandHelping, IdCard, ListChecks, ListTodo, Rocket, BookOpen
} from "lucide-react";

const NAV = {
  "Super Admin": [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["placements", "Placements", GraduationCap],
    ["schools", "Schools", School],
    ["facilities", "Facilities", Building2],
    ["paperwork", "Paperwork", FileCheck2],
    ["videos", "Video Calls", Video],
    ["calendar", "Calendar", CalendarDays],
    ["recruiting", "Recruiting", Briefcase],
    ["touchpoints", "Touchpoints", Coffee],
    ["engagement", "Engagement", Megaphone],
    ["analytics", "Analytics", BarChart3],
    ["templates", "Templates", FileText],
    ["student-tasks", "Student Tasks", ListTodo],
    ["huddle", "Student Huddle", Users],
    ["resources", "Resources", BookOpen],
    ["lists", "Lists & Options", ListChecks],
    ["users", "Users", Users],
    ["audit", "Audit Log", ScrollText],
    ["readiness", "Go-Live Checklist", Rocket],
    ["settings", "Settings", Settings],
  ],
  "Campus Coordinator": [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["placements", "Placements", GraduationCap],
    ["schools", "Schools", School],
    ["facilities", "Facilities", Building2],
    ["users", "Users", Users],
    ["paperwork", "Paperwork", FileCheck2],
    ["videos", "Video Calls", Video],
    ["calendar", "Calendar", CalendarDays],
    ["recruiting", "Recruiting", Briefcase],
    ["touchpoints", "Touchpoints", Coffee],
    ["engagement", "Engagement", Megaphone],
    ["analytics", "Analytics", BarChart3],
    ["templates", "Templates", FileText],
    ["student-tasks", "Student Tasks", ListTodo],
    ["huddle", "Student Huddle", Users],
    ["resources", "Resources", BookOpen],
    ["lists", "Lists & Options", ListChecks],
    ["audit", "Audit Log", ScrollText],
    ["readiness", "Go-Live Checklist", Rocket],
    ["my-bio", "My Bio", IdCard],
  ],
  "School User": [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["placements", "Placement Requests", GraduationCap],
    ["students", "Current Students", Users],
    ["schools", "School Profile", School],
    ["engagement", "Request Event", Megaphone],
    ["my-bio", "My Bio", IdCard],
  ],
  "Student": [
    ["dashboard", "My Dashboard", LayoutDashboard],
    ["paperwork", "My Paperwork", FileCheck2],
    ["videos", "Video Calls", Video],
    ["resources", "Resources", BookOpen],
  ],
  "DOR": [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["placements", "Placement Requests", GraduationCap],
    ["open-requests", "Open Requests", HandHelping],
    ["students", "Facility Students", Users],
    ["clinical-instructors", "Clinical Instructors", Stethoscope],
    ["facilities", "Facility Profile", Building2],
    ["resources", "Resources", BookOpen],
    ["my-bio", "My Bio", IdCard],
  ],
  "Clinical Instructor": [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["facilities", "Facility Profile", Building2],
    ["resources", "Resources", BookOpen],
    ["my-bio", "My Bio", IdCard],
  ],
  "Recruiter": [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["recruiting", "Pipeline", Briefcase],
    ["touchpoints", "Touchpoints", Coffee],
    ["analytics", "Analytics", BarChart3],
  ],
  "Area Manager": [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["reports", "Reports", BarChart3],
  ],
};
// Campus Coordinator Supervisor sees everything a Campus Coordinator does.
NAV["Campus Coordinator Supervisor"] = NAV["Campus Coordinator"];

const BIO_PROMPT_ROLES = ["Clinical Instructor", "DOR", "Campus Coordinator", "Campus Coordinator Supervisor", "School User"];

function BioPrompt() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bio, setBio] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!BIO_PROMPT_ROLES.includes(user?.role)) return;
    api.get("/bios/me").then(({ data }) => setBio(data)).catch(() => {});
  }, [user?.role]);
  if (!bio || bio.completed || dismissed) return null;
  return (
    <div className="mb-5 flex items-center justify-between rounded-md border-l-4 border-cc-blue bg-blue-50 px-5 py-3" data-testid="bio-prompt-banner">
      <div className="flex items-center gap-3">
        <IdCard className="w-5 h-5 text-cc-blue" />
        <span className="font-medium text-blue-900">
          {bio.kind === "ci" ? "Complete your CI bio so we can introduce you to incoming students and share your site details." : "Add a short bio so the campus team and schools know a bit about you."}
        </span>
      </div>
      <div className="flex items-center gap-4">
        <button onClick={() => navigate("/my-bio")} className="text-sm font-semibold text-cc-blue underline" data-testid="bio-prompt-cta">Fill it out</button>
        <button onClick={() => setDismissed(true)} className="text-zinc-400 hover:text-zinc-600" data-testid="bio-prompt-dismiss"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, stopImpersonate } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [guestCount, setGuestCount] = useState(0);
  const [version, setVersion] = useState(null);
  const [helpPip, setHelpPip] = useState(false);
  const canManageUsers = ["Super Admin", "Campus Coordinator", "Campus Coordinator Supervisor"].includes(user?.role);

  const loadNotifs = async () => {
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch {}
  };
  const loadGuestCount = async () => {
    try { const { data } = await api.get("/users/pending-guests-count"); setGuestCount(data.count || 0); } catch {}
  };
  useEffect(() => { api.get("/version").then(({ data }) => setVersion(data)).catch(() => {}); }, []);

  // Small dot on Help Center whenever a new feature shipped; clears once they open it.
  useEffect(() => {
    if (location.pathname === "/help") { markChangelogSeen(user?.role); setHelpPip(false); }
    else setHelpPip(hasUnseenChangelog(user?.role));
  }, [location.pathname, user?.role]);

  const endImpersonation = async () => { try { await stopImpersonate(); navigate("/dashboard"); } catch {} };
  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 20000);
    // Any component that marks messages read (event thread, messages page) fires this.
    const onChanged = () => loadNotifs();
    window.addEventListener("cc:notifs-changed", onChanged);
    if (canManageUsers) {
      loadGuestCount();
      const g = setInterval(loadGuestCount, 30000);
      return () => { clearInterval(t); clearInterval(g); window.removeEventListener("cc:notifs-changed", onChanged); };
    }
    return () => { clearInterval(t); window.removeEventListener("cc:notifs-changed", onChanged); };
  }, []);

  const unread = notifs.filter((n) => n.status === "unread");
  const hasUrgent = unread.some((n) => n.urgent);
  const messageNotifs = unread.filter((n) => n.kind === "message" || (n.subject || "").startsWith("New message"));
  const messageLink = messageNotifs[0]?.link || "/messages";
  const nav = [...(NAV[user?.role] || NAV["Student"]),
    ...(user?.role === "Area Manager" ? [] : [["messages", "Messages", MessageSquare]]),
    ["help", "Help Center", HelpCircle]];

  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };

  // Clicking the banner takes them to that conversation and stops it flashing.
  const openMessages = async () => {
    const n = messageNotifs[0];
    navigate(messageLink);
    try { await api.post(`/notifications/${n.id}/read`); } catch {}
    loadNotifs();
  };

  // Landing on Messages means they've read them — clear every direct-message alert.
  useEffect(() => {
    if (location.pathname !== "/messages") return;
    api.post("/notifications/read-messages", { prefix: "/messages" })
      .then(loadNotifs).catch(() => {});
  }, [location.pathname]);


  return (
    <div className="flex min-h-screen bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-screen w-64 bg-[#0A0A0A] text-zinc-300 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-4 py-6 border-b border-white/10 bg-white">
          <img src={LOGOS.campus} alt="Campus Connections" className="h-14 w-auto mx-auto object-contain" data-testid="sidebar-logo" />
        </div>
        <nav className="flex-1 overflow-y-auto cc-scroll px-3 py-4 space-y-1">
          {nav.map(([path, label, Icon]) => {
            const active = location.pathname === `/${path}` || (path === "dashboard" && location.pathname === "/");
            return (
              <Link key={path} to={`/${path}`} onClick={() => setOpen(false)}
                data-testid={`nav-${path}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${active ? "bg-cc-blue text-white" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}>
                <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
                <span className="flex-1">{label}</span>
                {path === "users" && guestCount > 0 && (
                  <span className="bg-cc-orange text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="users-guest-badge">{guestCount}</span>
                )}
                {path === "help" && helpPip && (
                  <span className="relative flex w-2.5 h-2.5" data-testid="help-whatsnew-pip" title="New features shipped">
                    <span className="absolute inline-flex w-full h-full rounded-full bg-cc-orange opacity-70 animate-ping" />
                    <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-cc-orange" />
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-white/10">
          <img src={LOGOS.tmc} alt="TMC" className="h-10 object-contain opacity-90 mb-3" />
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          {version && <p className="mt-3 text-[10px] text-zinc-600 font-mono" data-testid="app-version">v{version.version || APP_VERSION} · build {version.commit}</p>}
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 border-b border-border h-16 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle"><Menu className="w-6 h-6" /></button>
            <ProfilePhotoButton />
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-semibold">{user?.role}</div>
              <div className="text-sm font-medium text-zinc-700">
                {user?.name}
                {user?.organization_name && <span className="text-zinc-400 font-normal"> · {user.organization_name}</span>}
              </div>
              <div className="text-[10px] text-zinc-400 font-mono leading-tight" data-testid="app-version-badge">v{APP_VERSION}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <MyStatesButton />
            <HelpPanel />
            <div className="relative">
              <button onClick={() => setShowNotifs((s) => !s)} data-testid="notifications-bell"
                className={`relative p-2 rounded-md hover:bg-zinc-100 ${hasUrgent ? "cc-flash rounded-md" : ""}`}>
                <Bell className="w-5 h-5 text-zinc-700" />
                {unread.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-cc-orange text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{unread.length}</span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-border rounded-md shadow-lg z-50" data-testid="notifications-panel">
                  <div className="flex items-center justify-between px-4 py-2 border-b">
                    <span className="text-sm font-semibold">Notifications</span>
                    <button className="text-xs text-cc-blue" onClick={markAll} data-testid="mark-all-read">Mark all read</button>
                  </div>
                  <div className="max-h-96 overflow-y-auto cc-scroll">
                    {notifs.length === 0 && <div className="px-4 py-6 text-sm text-zinc-400 text-center">No notifications</div>}
                    {notifs.map((n) => (
                      <div key={n.id} className={`px-4 py-3 border-b last:border-0 ${n.status === "unread" ? "bg-blue-50/40" : ""}`}>
                        <div className="flex items-center gap-2">
                          {n.urgent && <span className="w-2 h-2 rounded-full bg-cc-orange cc-urgent" />}
                          <span className="text-sm font-medium text-zinc-800">{n.subject}</span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5">{n.body}</p>
                      </div>
                    ))}
                  </div>
                  <button className="w-full text-center text-xs text-cc-blue font-medium py-2.5 border-t hover:bg-zinc-50" data-testid="view-all-notifications" onClick={() => { setShowNotifs(false); navigate("/notifications"); }}>View all notifications</button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 max-w-[1600px] w-full mx-auto">
          {user?.impersonator && (
            <div className="mb-5 flex items-center justify-between rounded-md border-l-4 border-amber-500 bg-amber-50 px-5 py-3" data-testid="impersonation-banner">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-amber-600" />
                <span className="font-medium text-amber-900">Viewing as <b>{user.name}</b> ({user.role}) — impersonated by {user.impersonator.name}</span>
              </div>
              <button onClick={endImpersonation} data-testid="stop-impersonation-btn" className="text-sm font-semibold text-amber-700 hover:text-amber-900 underline">Stop impersonating</button>
            </div>
          )}
          {messageNotifs.length > 0 && location.pathname !== "/messages" && (
            <div onClick={openMessages} data-testid="new-message-banner"
              className="cc-flash cursor-pointer mb-5 flex items-center justify-between rounded-md border-l-4 border-cc-orange bg-orange-50 px-5 py-3">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-cc-orange" />
                <span className="font-medium text-orange-800">
                  {messageNotifs[0].subject?.startsWith("New message")
                    ? `You have ${messageNotifs.length} new message${messageNotifs.length > 1 ? "s" : ""} to view`
                    : `${messageNotifs[0].subject}${messageNotifs.length > 1 ? ` (+${messageNotifs.length - 1} more)` : ""}`}
                </span>
              </div>
              <span className="text-sm font-semibold text-cc-orange">{messageLink.startsWith("/engagement") ? "Open the event thread →" : "Open Messages →"}</span>
            </div>
          )}
          <BioPrompt />
          <Outlet />
        </main>
      </div>

      {/* Prominent floating Help button — draggable to any corner */}
      {location.pathname !== "/help" && <FloatingHelpButton />}
    </div>
  );
}
